import React, { useState, useEffect } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X, FileText, Eye, Columns, Loader2 } from 'lucide-react'
import { DocumentDialogRouter, createDefaultConfigs, createStandardSections } from './DocumentDialogRouter'
import type { ShipmentDocument } from '@wove/api'
import { getDocumentDownloadUrl } from '@wove/api'
import { Button, Tabs, TabsList, TabsTrigger, TabsContent } from '@wove/web-ui'

export interface SplitScreenDocumentDialogProps {
  isOpen: boolean
  onClose: () => void
  shipmentId: string
  document: ShipmentDocument
  onUpdate?: () => void
  useDocumentHistory?: (shipmentId: string, documentId: string) => any
  useUpdateDocument?: (shipmentId: string, documentId: string) => any
}

type ViewMode = 'original' | 'extracted' | 'side-by-side'

// Component to securely display documents
const DocumentViewer: React.FC<{ shipmentId: string; documentId: string; name: string; mimeType?: string }> = ({
  shipmentId,
  documentId,
  name,
  mimeType,
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [documentType, setDocumentType] = useState<string>('')

  useEffect(() => {
    const loadDocument = async () => {
      const downloadUrl = getDocumentDownloadUrl(shipmentId, documentId)
      const token = localStorage.getItem('token')

      if (!token) {
        setError('No authentication token found')
        setLoading(false)
        return
      }

      try {
        console.log('Loading document from:', downloadUrl)
        const response = await fetch(downloadUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.status} ${response.statusText}`)
        }

        const blob = await response.blob()
        console.log('Document blob:', blob.type, blob.size)
        setDocumentType(blob.type)

        const url = window.URL.createObjectURL(blob)
        console.log('Created blob URL:', url)
        setDocumentUrl(url)
      } catch (err) {
        console.error('Error loading document:', err)
        setError(err instanceof Error ? err.message : 'Failed to load document')
      } finally {
        setLoading(false)
      }
    }

    loadDocument()

    // Cleanup
    return () => {
      if (documentUrl) {
        window.URL.revokeObjectURL(documentUrl)
      }
    }
  }, [shipmentId, documentId])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin h-8 w-8 text-gray-500" />
        <span className="ml-2 text-gray-500">Loading document...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500">Error loading document</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    )
  }

  // For PDFs and images, we can display directly in iframe
  // For other types, we might need to show a download link
  const canDisplayInline =
    documentType.includes('pdf') || documentType.includes('image') || documentType.includes('text')

  if (!canDisplayInline) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <FileText className="h-16 w-16 text-gray-400 mb-4" />
        <p className="text-gray-600 mb-4">This document type cannot be displayed in the browser</p>
        <a
          href={documentUrl || ''}
          download={name}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Download {name}
        </a>
      </div>
    )
  }

  // For PDFs, append parameters to customize the viewer
  const getDisplayUrl = () => {
    if (!documentUrl) return ''

    if (documentType.includes('pdf')) {
      // Add PDF viewer parameters:
      // - navpanes=0: hide the navigation panes (sidebar)
      // - toolbar=1: keep the toolbar (for zoom controls)
      // - scrollbar=1: keep the scrollbar
      // - view=FitH: fit horizontally
      return `${documentUrl}#navpanes=0&toolbar=1&scrollbar=1&view=FitH`
    }

    return documentUrl
  }

  return <iframe src={getDisplayUrl()} className="w-full h-full border-0" title={name} />
}

export const SplitScreenDocumentDialog: React.FC<SplitScreenDocumentDialogProps> = ({
  isOpen,
  onClose,
  shipmentId,
  document,
  onUpdate,
  useDocumentHistory,
  useUpdateDocument,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('side-by-side')
  const [splitPosition, setSplitPosition] = useState(50) // Percentage for left panel
  const [isDragging, setIsDragging] = useState(false)

  // Create default configurations
  const configs = createDefaultConfigs()

  // Use the extracted data from the document
  const documentData = document.extractedData || {}

  // Generate schema and sections for the document type
  const schema = generateSchemaFromData(documentData)
  const sections = createStandardSections(document.type, schema)

  // Update the config for this document type
  if (configs[document.type]) {
    configs[document.type].schema = schema
    configs[document.type].sections = sections
  }

  // Create a fallback config for unknown document types
  const fallbackConfig = {
    documentType: document.type.replace(/([A-Z])/g, ' $1').trim(),
    schema,
    sections,
  }

  const handleSave = (updatedData: any, changes?: any[]) => {
    onUpdate?.()
  }

  // Handle mouse events for resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return

    // Find the container element
    const container = window.document.querySelector('.split-container')
    if (!container) return

    const rect = container.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percentage = (x / rect.width) * 100

    // Limit the split position between 20% and 80%
    setSplitPosition(Math.min(80, Math.max(20, percentage)))
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Add and remove mouse event listeners
  useEffect(() => {
    if (typeof window === 'undefined' || !window.document || !window.document.body) return

    if (isDragging) {
      const handleMouseMoveWrapper = (e: MouseEvent) => handleMouseMove(e)
      const handleMouseUpWrapper = () => handleMouseUp()

      // Add cursor style to body
      const body = window.document.body
      const originalCursor = body.style.cursor
      const originalUserSelect = body.style.userSelect

      body.style.cursor = 'col-resize'
      body.style.userSelect = 'none'

      window.addEventListener('mousemove', handleMouseMoveWrapper)
      window.addEventListener('mouseup', handleMouseUpWrapper)

      return () => {
        window.removeEventListener('mousemove', handleMouseMoveWrapper)
        window.removeEventListener('mouseup', handleMouseUpWrapper)
        // Restore original styles
        body.style.cursor = originalCursor
        body.style.userSelect = originalUserSelect
      }
    }
  }, [isDragging])

  const renderContent = () => {
    switch (viewMode) {
      case 'original':
        return (
          <div className="h-full w-full">
            <DocumentViewer
              shipmentId={shipmentId}
              documentId={document.id}
              name={document.name}
              mimeType={document.mimeType}
            />
          </div>
        )

      case 'extracted':
        return (
          <div className="h-full w-full overflow-hidden">
            <DocumentDialogRouter
              shipmentId={shipmentId}
              documentId={document.id}
              document={{
                ...document,
                extractedData: documentData,
              }}
              configs={configs}
              fallbackConfig={fallbackConfig}
              useDocumentHistory={useDocumentHistory}
              useUpdateDocument={useUpdateDocument}
              onSave={handleSave}
            />
          </div>
        )

      case 'side-by-side':
        return (
          <div className={`h-full w-full flex relative split-container ${isDragging ? 'select-none' : ''}`}>
            {/* Invisible overlay during dragging to prevent iframe interference */}
            {isDragging && <div className="absolute inset-0 z-20" style={{ cursor: 'col-resize' }} />}
            <div className="h-full" style={{ width: `${splitPosition}%` }}>
              <div className="h-full flex flex-col">
                <div className="px-4 py-2 border-b bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-700">Original Document</h3>
                </div>
                <div className="flex-1">
                  <DocumentViewer
                    shipmentId={shipmentId}
                    documentId={document.id}
                    name={document.name}
                    mimeType={document.mimeType}
                  />
                </div>
              </div>
            </div>

            {/* Resizer */}
            <div
              className={`absolute top-0 bottom-0 z-10 group ${isDragging ? 'bg-blue-500' : ''}`}
              style={{
                left: `${splitPosition}%`,
                cursor: 'col-resize',
                transform: 'translateX(-50%)',
                width: '4px',
              }}
              onMouseDown={handleMouseDown}
            >
              <div className="absolute inset-y-0 -left-2 -right-2 group-hover:bg-blue-400/20" />
              <div
                className={`absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 ${
                  isDragging ? 'bg-blue-500' : 'bg-gray-300 group-hover:bg-blue-500'
                } transition-colors`}
              />
            </div>

            <div className="h-full" style={{ width: `${100 - splitPosition}%` }}>
              <div className="h-full flex flex-col">
                <div className="px-4 py-2 border-b bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-700">Extracted Data</h3>
                </div>
                <div className="flex-1 overflow-hidden">
                  <DocumentDialogRouter
                    shipmentId={shipmentId}
                    documentId={document.id}
                    document={{
                      ...document,
                      extractedData: documentData,
                    }}
                    configs={configs}
                    fallbackConfig={fallbackConfig}
                    useDocumentHistory={useDocumentHistory}
                    useUpdateDocument={useUpdateDocument}
                    onSave={handleSave}
                  />
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] overflow-hidden"
          style={{ width: '95vw', maxWidth: '95vw', height: '90vh', maxHeight: '90vh' }}
        >
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h2 className="text-lg font-semibold">{document.name || document.type}</h2>
              <div className="flex items-center gap-4">
                <div className="flex gap-1 p-1 bg-gray-100 rounded-md">
                  <Button
                    variant={viewMode === 'original' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('original')}
                    className="px-3 py-1"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Original
                  </Button>
                  <Button
                    variant={viewMode === 'extracted' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('extracted')}
                    className="px-3 py-1"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Extracted
                  </Button>
                  <Button
                    variant={viewMode === 'side-by-side' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('side-by-side')}
                    className="px-3 py-1"
                  >
                    <Columns className="w-4 h-4 mr-1" />
                    Side by Side
                  </Button>
                </div>
                <DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">{renderContent()}</div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// Helper function to generate a basic schema from extracted data
function generateSchemaFromData(data: any): any {
  const properties: Record<string, any> = {}

  if (data && typeof data === 'object') {
    Object.keys(data).forEach(key => {
      const value = data[key]

      if (Array.isArray(value)) {
        properties[key] = {
          type: 'array',
          items: value.length > 0 ? generateSchemaFromData(value[0]) : { type: 'object' },
        }
      } else if (value && typeof value === 'object') {
        properties[key] = {
          type: 'object',
          properties: generateSchemaFromData(value).properties,
        }
      } else if (typeof value === 'number') {
        properties[key] = { type: 'number' }
      } else if (typeof value === 'boolean') {
        properties[key] = { type: 'boolean' }
      } else {
        properties[key] = { type: 'string' }
      }
    })
  }

  return {
    type: 'object',
    properties,
  }
}
